import { useMemo } from 'react';
import {
  findCoding,
  findIdentifier,
  formatCodingToken,
} from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { useDataset, useFHIRQuery } from '../../../../util/api';
import type { MyOmeMissingField } from '../../../../util/genomicIngestionApi';
import { getMyOmeRqId } from '../../../../util/myome';

const MYOME_REQUISITION_ROLE_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-role';
const MYOME_REQUISITION_STATUS_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-status';
const MYOME_REQUISITION_REASON_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-reason';
const MYOME_REQUISITION_ID_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-id';
const MYOME_REQUISITION_INPUT_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-input';
const ELATION_VENDOR_ID_SYSTEM =
  'http://lifeomic.com/fhir/extension/ehr-elation-vendor-id';
const MYOME_TEST_CODE_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-test-code';
const MYOME_ORDER_STATUS_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-order-status';
const MYOME_TEST_CODES = [
  'MYOME-BLOOD-CENTER',
  'MYOME-BUCCAL-SWAB-HOME',
] as const;
const MISSING_FIELD_SOURCES: MyOmeMissingField['source'][] = [
  'survey',
  'lab-or-vital',
  'consent',
];

export type MyOmeTestCode = (typeof MYOME_TEST_CODES)[number];

const isMyOmeTestCode = (code: string | undefined): code is MyOmeTestCode =>
  MYOME_TEST_CODES.some(known => known === code);

const isMissingFieldSource = (
  code: string | undefined,
): code is MyOmeMissingField['source'] =>
  MISSING_FIELD_SOURCES.some(known => known === code);

const inputsOfType = (task: fhir3.Task, code: string) =>
  (task.input ?? []).filter(input =>
    input.type?.coding?.some(
      coding =>
        coding.system === MYOME_REQUISITION_INPUT_SYSTEM &&
        coding.code === code,
    ),
  );

// Tasks projected before GIS recorded the test have no test-code input, so
// the input type isn't required here.
const getMyOmeTestCode = (task: fhir3.Task): MyOmeTestCode | undefined =>
  task.input
    ?.map(({ valueCoding }) => valueCoding)
    .filter(coding => coding?.system === MYOME_TEST_CODE_SYSTEM)
    .map(coding => coding?.code)
    .find(isMyOmeTestCode);

const getMissingFields = (task: fhir3.Task): MyOmeMissingField[] =>
  inputsOfType(task, 'missing-field').flatMap(({ valueCoding }) =>
    valueCoding?.display && isMissingFieldSource(valueCoding.code)
      ? [{ name: valueCoding.display, source: valueCoding.code }]
      : [],
  );

/**
 * patient-service only sorts by `period` when the query also filters on it, so
 * the lower bound is far enough in the past to never exclude a Task.
 */
const ALL_PERIODS = 'ge2000-01-01';

/**
 * One of the patient's MyOme requisitions, as projected by
 * genomic-ingestion-service. `statusReason.text` is GIS's own wording, which
 * clinicians don't see, so it's deliberately not exposed.
 */
export type MyOmeRequisition = {
  taskId: string;
  /** The GIS row id the re-run route takes, such as `elation-lab-order:123`. */
  requisitionId: string | undefined;
  /** An iPRS order reuses its original's sample and never prints. */
  kind: 'original' | 'iprs';
  /** MyOme's RQ id. Absent until MyOme accepts the order. Treat as opaque. */
  rqId: string | undefined;
  status: fhir3.Task['status'];
  /** The exact GIS requisition status, such as `AWAITING_GATES` or `OPEN`. */
  businessStatus: string | undefined;
  /** A stable GIS reason code, such as `consent-missing`. */
  reasonCode: string | undefined;
  elationLabOrderId: string | undefined;
  /** The Elation test GIS placed, or `undefined` on Tasks from before GIS recorded it. */
  testCode: MyOmeTestCode | undefined;
  /**
   * How the sample is collected, from the Elation test GIS placed. Tasks
   * without a test code predate buccal orders and were all blood. An iPRS
   * order takes its original's.
   */
  collection: 'blood-on-site' | 'buccal-home-kit';
  /**
   * Whether GIS would act on a re-run. `undefined` on Tasks projected before
   * GIS recorded it.
   */
  rerunnable: boolean | undefined;
  /** Why a FAILED order failed, such as `vendor-rejected`. */
  failureClass: string | undefined;
  /** What an iPRS order is waiting on, for `iprs-package-incomplete`. */
  missing: MyOmeMissingField[];
  /**
   * MyOme's own status for a placed order, such as `ANALYZING`, as GIS last
   * polled it. `undefined` until GIS has seen the order.
   */
  myomeStatus: string | undefined;
  /** When GIS saw `myomeStatus` change, as an ISO date-time. */
  myomeStatusChangedAt: string | undefined;
};

export type MyOmeRequisitionResult =
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'error'; error: unknown; retry: () => void }
  | {
      state: 'found';
      /**
       * At most one original per collection, in `COLLECTION_ORDER`, each
       * followed by its iPRS order once the original is placed. Never empty.
       */
      requisitions: MyOmeRequisition[];
      refetch: () => void;
    };

const COLLECTION_ORDER: MyOmeRequisition['collection'][] = [
  'blood-on-site',
  'buccal-home-kit',
];

/** The original is placed, so its iPRS order is worth showing. */
const PLACED_STATUSES = new Set(['OPEN', 'REPORT_CREATED']);

const toMyOmeRequisition = (
  task: fhir3.Task,
  kind: MyOmeRequisition['kind'],
): MyOmeRequisition => {
  const testCode = getMyOmeTestCode(task);
  return {
    taskId: task.id ?? '',
    requisitionId: findIdentifier(task.identifier, {
      system: MYOME_REQUISITION_ID_SYSTEM,
    })?.value,
    kind,
    rqId: getMyOmeRqId(task),
    status: task.status,
    businessStatus: findCoding(task.businessStatus?.coding, {
      system: MYOME_REQUISITION_STATUS_SYSTEM,
    })?.code,
    reasonCode: findCoding(task.statusReason?.coding, {
      system: MYOME_REQUISITION_REASON_SYSTEM,
    })?.code,
    elationLabOrderId: findIdentifier(task.identifier, {
      system: ELATION_VENDOR_ID_SYSTEM,
    })?.value,
    testCode,
    collection:
      testCode === 'MYOME-BUCCAL-SWAB-HOME'
        ? 'buccal-home-kit'
        : 'blood-on-site',
    rerunnable: inputsOfType(task, 'rerunnable')[0]?.valueBoolean,
    failureClass: inputsOfType(task, 'failure-class')[0]?.valueCoding?.code,
    missing: getMissingFields(task),
    myomeStatus: inputsOfType(task, 'myome-status')
      .map(({ valueCoding }) => valueCoding)
      .find(coding => coding?.system === MYOME_ORDER_STATUS_SYSTEM)?.code,
    myomeStatusChangedAt: inputsOfType(task, 'myome-status-changed-at')[0]
      ?.valueDateTime,
  };
};

/** GIS sets `executionPeriod.start` to when the requisition was created. */
const getCreatedAt = (task: fhir3.Task): number => {
  const createdAt = Date.parse(task.executionPeriod?.start ?? '');
  return Number.isNaN(createdAt) ? -Infinity : createdAt;
};

/** Ties keep the earlier Task, so the server's `-period` order breaks them. */
const latestBy = <Key>(
  tasks: fhir3.Task[],
  keyOf: (task: fhir3.Task) => Key | undefined,
): Map<Key, fhir3.Task> => {
  const latest = new Map<Key, fhir3.Task>();
  for (const task of tasks) {
    const key = keyOf(task);
    if (task.status === 'entered-in-error' || key === undefined) {
      continue;
    }
    const current = latest.get(key);
    if (!current || getCreatedAt(task) > getCreatedAt(current)) {
      latest.set(key, task);
    }
  }
  return latest;
};

/**
 * The latest live original per collection, blood first, each followed by the
 * latest live iPRS Task that is `partOf` it once the original is placed.
 * `sample-reuse` Tasks without a `partOf` are later Elation orders, not iPRS.
 */
export const selectLatestMyOmeRequisitions = (
  originalTasks: fhir3.Task[],
  reuseTasks: fhir3.Task[] = [],
): MyOmeRequisition[] => {
  const originals = latestBy(
    originalTasks,
    task => toMyOmeRequisition(task, 'original').collection,
  );
  const iprsByOriginal = latestBy(
    reuseTasks,
    task => task.partOf?.[0]?.reference,
  );
  return COLLECTION_ORDER.flatMap(collection => {
    const task = originals.get(collection);
    if (!task) {
      return [];
    }
    const original = toMyOmeRequisition(task, 'original');
    const iprsTask = iprsByOriginal.get(`Task/${original.taskId}`);
    return iprsTask && PLACED_STATUSES.has(original.businessStatus ?? '')
      ? [original, { ...toMyOmeRequisition(iprsTask, 'iprs'), collection }]
      : [original];
  });
};

const useRequisitionTaskQuery = (
  patientId: string,
  role: 'sample-collection' | 'sample-reuse',
  enabled: boolean,
) => {
  const dataset = useDataset();
  return useFHIRQuery(
    'GET /v1/fhir/dstu3/Task',
    {
      _tag: dataset.codingToken,
      patient: patientId,
      // Dev FHIR ignores `part-of`, so iPRS Tasks are linked client-side.
      code: formatCodingToken({
        system: MYOME_REQUISITION_ROLE_SYSTEM,
        code: role,
      }),
      period: ALL_PERIODS,
      _sort: '-period',
      // One page has to reach both collections' latest Tasks.
      pageSize: 200,
    },
    // A clinician waiting on MyOme returns to the member expecting the RQ id,
    // so don't serve a cached requisition without checking for a newer one.
    { enabled, staleTime: 0 },
  );
};

/**
 * Finds the patient's latest original requisition Task for each collection
 * (on-site blood, buccal home kit), whatever its status, and the iPRS order
 * that reuses each one's sample.
 */
export const useMyOmeRequisition = (
  patientId: string,
): MyOmeRequisitionResult => {
  const dataset = useDataset();
  const enabled = Boolean(patientId) && Boolean(dataset.id);
  const originals = useRequisitionTaskQuery(
    patientId,
    'sample-collection',
    enabled,
  );
  const reuses = useRequisitionTaskQuery(patientId, 'sample-reuse', enabled);

  const {
    data,
    isError,
    isFetching,
    error,
    refetch: refetchOriginals,
  } = originals;
  const { data: reuseData, refetch: refetchReuses } = reuses;

  return useMemo((): MyOmeRequisitionResult => {
    if (!enabled) {
      return { state: 'none' };
    }
    const refetch = () => {
      void refetchOriginals();
      void refetchReuses();
    };
    // The iPRS search only adds tiles, so it never holds back the original's.
    if (data) {
      const requisitions = selectLatestMyOmeRequisitions(
        unwrapBundle(data),
        reuseData ? unwrapBundle(reuseData) : [],
      );
      return requisitions.length > 0
        ? { state: 'found', requisitions, refetch }
        : { state: 'none' };
    }
    if (isError && !isFetching) {
      return { state: 'error', error, retry: refetch };
    }
    return { state: 'loading' };
  }, [
    enabled,
    data,
    reuseData,
    isError,
    isFetching,
    error,
    refetchOriginals,
    refetchReuses,
  ]);
};
